<!--
The MediaUploader component is a custom Vue component for uploading files to a temporary S3 bucket.
It uses the [vue2-dropzone] library and allows for drag-and-drop of files as well as uploading files via a file dialog.
The uploaded files can be further processed by other components. See event: [uploads].

## Slots

The component has a single slot that accepts the following attributes:

- [totalProgress]: The total progress of the upload process in percentage.
- [parallelUploads]: The number of files uploading simultaneously.
- [valid]: A Boolean value indicating whether the uploads were successful.
- [removeUpload]: A method for removing an uploaded file from the list of all uploaded files.
- [validate]: A method for validating all the uploaded files.

The slot can be used to integrate custom content and functionality into the component.
For Example: A custom display of the uploaded files.

## Events

The MediaUploader component utilizes the following events to react to various stages of the upload process:

- [progress]: Emits the total progress of the upload process in percentage.
- [uploads]: Emits an Array of all uploaded files.
- [valid]: Emits a Boolean value indicating whether the uploads were successful.

-->

<template>
  <div>
    <vue-dropzone
      ref="dropzone"
      id="attachmentDropzone"
      :key="dropzoneOptions.url"
      :options="dropzoneOptions"
      :include-styling="false"
      :useCustomSlot="true"
      v-on="handlers"
    >
      <div class="dropzone-content">
        <v-icon color="primary" class="ma-8" :size="48">{{
          mdiFileUploadOutline
        }}</v-icon>
        <div class="py-2 pr-2">
          <Header
            color="primary"
            customClass="mb-0"
            :text="
              titleOverride
                ? titleOverride
                : t$('components.attachment_dropzone.dropzone_title')
            "
            :level="5"
          ></Header>
          <TextBody class="mt-4" color="primary">{{
            t$("components.attachment_dropzone.dropzone_subtitle")
          }}</TextBody>
        </div>
      </div>
    </vue-dropzone>
    <slot
      :totalProgress="totalProgress"
      :parallelUploads="dropzoneOptions.parallelUploads"
      :valid="valid"
      :removeUpload="removeUpload"
      :validate="validate"
    ></slot>
  </div>
</template>

<script lang="ts">
import Vue, { defineComponent, inject, PropType } from "vue"
import { DropzoneUploadFile, MediaUpload } from "./media.types"
import useTranslate from "features/common/translate/concerns/useTranslate"
import TextBody from "components/UI/TextBody.vue"
import Header from "components/UI/Header.vue"
import vue2Dropzone from "vue2-dropzone"
import { mdiFileUploadOutline } from "@mdi/js"
import { driveApi } from "store/api"
import { MediaUploaderService } from "features/media/media-uploader.service"
import { FileUploadUrlSignature } from "suus-api"
import { UploadWaitingList } from "features/media/upload-waiting-list"

// Required, otherwise Vue 2.7 gives compiler errors.
type VueDropzoneHandlers = {
  "vdropzone-file-added": (File) => void
  "vdropzone-thumbnail": (File, string) => void
  "vdropzone-processing": (File) => void
  "vdropzone-sending": (File) => void
  "vdropzone-success": (File, any) => void
  "vdropzone-error": (File, string) => void
  "vdropzone-upload-progress": (File, number) => void
  "vdropzone-queue-complete": () => void
}

const MediaUploaderComponent = defineComponent({
  name: "MediaUploader",
  props: {
    existingUploads: {
      type: Number,
      default: 0,
    },
    onlyImages: {
      type: Boolean,
      default: false,
    },
    titleOverride: {
      type: String,
      default: undefined,
    },
    validateFileBeforeUpload: {
      type: Function as PropType<(file: File) => boolean>,
      default: () => true,
    },
  },
  components: {
    TextBody,
    Header,
    vueDropzone: vue2Dropzone,
  },
  computed: {
    uploadsArray(): MediaUpload[] {
      return Object.values(this.uploads)
    },
    totalProgress(): number {
      const progressSumFunc = (acc, el) => acc + el.uploadProgress
      const accInit = 0
      const progress =
        this.uploadsArray.reduce(progressSumFunc, accInit) /
        this.uploadsArray.length
      const result = isNaN(progress) ? 0 : progress
      if (result > 0 && result < 100) {
        this.$emit("saving", true)
      }
      this.$emit("progress", result)
      return result
    },
    // eslint-disable-next-line @typescript-eslint/ban-types
    handlers(): VueDropzoneHandlers {
      return {
        "vdropzone-file-added": (this as any).fileAdded,
        "vdropzone-thumbnail": (file, dataUrl: string | Event) => {
          if (typeof dataUrl === "object" && dataUrl instanceof Event) {
            // A event is returned in case no preview URL could be generated, e.g. for TIFF or RAW files
            this.updateUpload({ thumbDataUrl: null })(file.token)
          } else {
            this.updateUpload({ thumbDataUrl: dataUrl })(file.token)
          }
        },
        "vdropzone-processing": (file) =>
          this.updateUpload({ processing: true })(file.token),
        "vdropzone-sending": (file) =>
          this.updateUpload({ sending: true })(file.token),
        "vdropzone-success": (file, response) =>
          this.updateUpload({ success: response })(file.token),
        "vdropzone-error": (file, message) => {
          if (message === "You can't upload files of this type.") {
            this.removeUpload(file.token)
            return
          }
          this.updateUpload({ errors: [message] })(file.token)
        },
        "vdropzone-upload-progress": (file, progress) =>
          this.updateUpload({ uploadProgress: progress })(file.token),
        "vdropzone-queue-complete": () => {
          this.finishSaving()
          this.validate()
        },
      }
    },
  },
  data() {
    return {
      dropzoneOptions: {
        url: "toBeSetByGetSignedUrl", // avoid warning
        maxFiles: null, // limitation does not work, see https://github.com/rowanwins/vue-dropzone/issues/300
        maxFilesize: 2000, // megabytes. AWS signing limits the filesize
        maxThumbnailFilesize: 20, // mb
        previewTemplate: "<span></span>",
        createImageThumbnails: true,
        chunking: false,
        timeout: 1000 * 60 * 60, //timeout of 1h in ms for a single XHR request
        autoProcessQueue: true,
        acceptedFiles: this.onlyImages ? "image/*" : undefined, // not stable across browser, so we just check at server side
        // capture: "camera",
        dictResponseError: "Fehler beim Hochladen ({{statusCode}})!",
        parallelUploads: 1,
        headers: {
          "X-CSRF-Token":
            Vue.prototype.$http.defaults.headers.common["X-CSRF-Token"],
        },
        sending: (file, xhr, formData) =>
          (this as any).appendS3SignatureToFormData(file, xhr, formData),
        renameFile: (f) => (this as any).renameFile(f),
      },
      s3Signature: {} as FileUploadUrlSignature,
      valid: false,
      uploads: {} as { [k: string]: MediaUpload },
      timeout: 5000,
    }
  },
  mounted() {
    this.getPresignedS3Url()
    this.mediaSerivce?.addOnResetFunction(() => {
      this.uploadsArray.forEach((upload) => {
        this.removeUpload(upload.file.token)
      })
    })
  },
  setup() {
    const mediaSerivce: MediaUploaderService = inject("mediaService", null)
    const { tc$, t$ } = useTranslate("components.media_uploader")

    const waitingList = new UploadWaitingList()

    return { tc$, t$, mdiFileUploadOutline, mediaSerivce, waitingList }
  },
  methods: {
    async getPresignedS3Url(): Promise<void> {
      const { data } = await driveApi.getUploadURL()
      const { postEndpoint, signature } = data
      this.s3Signature = signature
      this.dropzoneOptions.url = postEndpoint
    },
    appendS3SignatureToFormData(
      file: DropzoneUploadFile,
      xhr: XMLHttpRequest,
      formData: FormData
    ): void {
      for (let [k, v] of Object.entries(this.s3Signature)) {
        formData.append(k, v as string)
      }
    },
    createToken(): string {
      return Math.random().toString(36).substring(2)
    },
    renameFile(file: DropzoneUploadFile): string {
      const splittedFile = file.name.split(".")
      if (splittedFile.length <= 1) {
        return this.createToken()
      }
      const ext = splittedFile.slice(-1)[0]
      return this.createToken() + "." + ext
    },
    fileAdded(file: DropzoneUploadFile) {
      const isFileValid = this.validateFileBeforeUpload(file)
      if (!isFileValid) {
        console.warn("File is not valid, removing it from the list.")
        this.removeFile(file)
        return
      }
      file.token = this.createToken()
      const upload: MediaUpload = {
        file: file,
        name: file.name,
        success: undefined,
        complete: false,
        errors: [],
        sending: false,
        canceled: false,
        processing: false,
        uploadProgress: 0,
        thumbDataUrl: undefined,
        formData: undefined,
      }
      this.$set(this.uploads, file.token, {
        ...this.uploads[file.token],
        ...upload,
      })
    },
    updateUpload(obj: Partial<MediaUpload>) {
      return (token) => {
        if (!this.uploads[token]) {
          return
        }
        if (obj) {
          const newUpload = { ...this.uploads[token], ...obj }
          // If success or errors are set, we finish the waiting
          if (obj.errors || obj.s3Key) {
            this.waitingList.finishWaiting(this.uploads[token].file, newUpload)
          }
          this.$set(this.uploads, token, newUpload)
        } else {
          this.removeUpload(token)
        }
        this.$emit("uploads", this.uploadsArray)
      }
    },
    removeUpload(token: string) {
      const upload = this.uploads[token]
      if (!upload) return
      if (upload.file) this.removeFile(upload.file)
      this.$delete(this.uploads, token)
      this.$emit("uploads", this.uploadsArray)
    },
    removeFile(file: DropzoneUploadFile) {
      ;(this.$refs.dropzone as any)?.removeFile(file)
    },
    handleUploadError(error: string, token: string) {
      this.updateUpload({ errors: [error] })(token)
    },
    finishSaving() {
      this.$emit("saving", false)
    },
    validate() {
      const isS3KeyAvailable = this.uploadsArray.every((u) => u.s3Key)
      if (!isS3KeyAvailable) {
        for (let upload of this.uploadsArray) {
          if (!upload.s3Key && upload.success) {
            try {
              const parser = new DOMParser()
              const xmlDoc = parser.parseFromString(
                upload.success,
                "text/xml"
              ).documentElement
              const s3Key = xmlDoc.getElementsByTagName("Key")[0].textContent
              this.updateUpload({ s3Key: s3Key })(upload.file.token)
            } catch (e: any) {
              this.updateUpload({ errors: [e.message] })(upload.file.token)
            }
          }
        }
      }
      const isNowS3KeyAvailable = this.uploadsArray.every((u) => u.s3Key)
      this.valid =
        (this.uploadsArray.length > 0 &&
          this.totalProgress === 100 &&
          isNowS3KeyAvailable) ||
        this.uploadsArray.length === 0
      this.$emit("valid", this.valid)
    },
    reset() {
      this.valid = false
      this.uploads = {}
    },
    retry(token: string) {
      const upload = this.uploads[token]
      upload.errors = []
      upload.file.status = "queued"
      ;(this.$refs.dropzone as any)?.processQueue()
    },
    // Returns a Promise which resolves to the s3/stored file key
    manuallyAddFileAndUpload(file: File): {
      storedFile: Promise<string>
      upload: DropzoneUploadFile
    } {
      const promise = this.waitingList.startWaiting(file)

      // Adding the file to dropzone turns it into a DropzoneUploadFile
      ;(this.$refs.dropzone as any)?.dropzone.addFile(file)

      return {
        storedFile: promise,
        upload: file as DropzoneUploadFile,
      }
    },
    manuallyOpenFileBrowser() {
      ;(this.$refs.dropzone as any)?.dropzone.hiddenFileInput.click()
    },
    manuallyDeleteUpload(tokenToDelete: string) {
      const upload = this.uploadsArray.find(
        (upload) => upload.file.token === tokenToDelete
      )
      if (upload) {
        this.removeFile(upload.file)
        this.uploads = Object.fromEntries(
          Object.entries(this.uploads).filter(
            ([token, _]: [string, MediaUpload]) => token !== tokenToDelete
          )
        )
      }
    },
    manuallyReset() {
      this.reset()
      ;(this.$refs.dropzone as any)?.dropzone.removeAllFiles()
    },
  },
  watch: {
    totalProgress: {
      immediate: true,
      handler() {
        this.validate()
      },
    },
    uploads() {
      this.validate()
    },
  },
})

type MediaUploaderType = InstanceType<typeof MediaUploaderComponent>
export { MediaUploaderType }
export default MediaUploaderComponent
</script>

<style>
#attachmentDropzone {
  cursor: pointer;
  border: #307ef7 1px dashed;
  border-radius: 4px;
  background: transparent;
  padding: 0;
  display: flex;
}

#attachmentDropzone.dz-drag-hover {
  border: #307ef7 3px solid;
}

.dropzone-content {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
